import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  StatusBadge,
  type StatusBadgeSeverity,
} from '../../../components/StatusBadge';
import type {
  Announcement,
  AnnouncementStatus,
} from '../../../hooks/api/useAnnouncements';

const STATUS_SEVERITY: Record<AnnouncementStatus, StatusBadgeSeverity> = {
  published: 'success',
  draft: 'warning',
  retired: 'neutral',
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const formatDate = (iso: string) => dayjs(iso).format('YYYY-MM-DD');

/** `start – now` while announced, `start – end (Nd)` once retired, `-` if never announced. */
export const formatActivePeriod = (
  t: TFunction,
  announcement: Announcement
): string => {
  const { published_at: publishedAt, retired_at: retiredAt } = announcement;
  if (!publishedAt) return '-';
  const start = formatDate(publishedAt);
  if (announcement.status === 'published') {
    return t('announcements.activeNow', { start });
  }
  if (!retiredAt) return start;
  const days = Math.max(
    1,
    Math.round(
      (new Date(retiredAt).getTime() - new Date(publishedAt).getTime()) /
        MS_PER_DAY
    )
  );
  return t('announcements.activeRange', {
    start,
    end: formatDate(retiredAt),
    days,
  });
};

/** The status as a static pill (StatusBadge without a child to anchor to). */
export const AnnouncementStatusBadge = ({
  status,
}: {
  status: AnnouncementStatus;
}) => {
  const { t } = useTranslation();
  return (
    <StatusBadge
      severity={STATUS_SEVERITY[status]}
      badgeContent={t(`announcements.status.${status}`)}
      sx={{
        // A different key from StatusBadge's own '& .MuiBadge-badge', so its
        // severity colours are kept rather than replaced.
        '& > .MuiBadge-badge': {
          position: 'static',
          transform: 'none',
          height: 'auto',
          minHeight: 22,
          px: 8,
          py: 2,
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 500,
          whiteSpace: 'nowrap',
        },
      }}
    />
  );
};
